import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Globe, MapPin, Building, Trash2, Plus, RefreshCw } from 'lucide-react';

export default function AdminDropdownManagement() {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [venueCenters, setVenueCenters] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [newCityName, setNewCityName] = useState('');
  const [newVenue, setNewVenue] = useState({ name: '', address: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [countryRes, cityRes, venueRes] = await Promise.all([
        axios.get(`${API}/countries`),
        axios.get(`${API}/cities`),
        axios.get(`${API}/venue-centers`)
      ]);

      setCountries(countryRes.data);
      setCities(cityRes.data);
      setVenueCenters(venueRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      setLoading(false);
    }
  };

  const getCountryCities = () => {
    if (!selectedCountry) return [];
    return cities.filter(c => c.country_id === selectedCountry.id);
  };

  const getCityVenues = () => {
    if (!selectedCity) return [];
    return venueCenters.filter(v => v.city_id === selectedCity);
  };

  const handleAddCity = async (e) => {
    e.preventDefault();
    if (!selectedCountry || !newCityName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cities`, {
        name: newCityName,
        country_id: selectedCountry.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Şehir eklendi');
      setNewCityName('');
      fetchData();
    } catch (error) {
      toast.error('Şehir eklenemedi');
    }
  };

  const handleDeleteCity = async (cityId) => {
    if (!window.confirm('Bu şehri silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cities/${cityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Şehir silindi');
      fetchData();
    } catch (error) {
      toast.error('Şehir silinemedi');
    }
  };

  const handleAddVenue = async (e) => {
    e.preventDefault();
    if (!selectedCity || !newVenue.name.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/venue-centers`, {
        name: newVenue.name,
        city_id: selectedCity,
        address: newVenue.address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fuar merkezi eklendi');
      setNewVenue({ name: '', address: '' });
      fetchData();
    } catch (error) {
      toast.error('Fuar merkezi eklenemedi');
    }
  };

  const handleDeleteVenue = async (venueId) => {
    if (!window.confirm('Bu fuar merkezini silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/venue-centers/${venueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fuar merkezi silindi');
      fetchData();
    } catch (error) {
      toast.error('Fuar merkezi silinemedi');
    }
  };

  return (
    <div className="p-8" data-testid="admin-dropdown-management">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dropdown Yönetimi</h1>
            <p className="text-gray-500 mt-1">Ülke, şehir ve fuar merkezi verilerini yönet</p>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
        </div>
      </div>

      <Tabs defaultValue="countries-cities" className="w-full">
        <TabsList>
          <TabsTrigger value="countries-cities" data-testid="tab-countries-cities">
            <Globe className="w-4 h-4 mr-2" />
            Ülkeler / Şehirler
          </TabsTrigger>
          <TabsTrigger value="venues" data-testid="tab-venues">
            <Building className="w-4 h-4 mr-2" />
            Fuar Merkezleri
          </TabsTrigger>
        </TabsList>

        {/* Countries and Cities Tab */}
        <TabsContent value="countries-cities" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Countries Column */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Ülkeler ({countries.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <div className="space-y-2">
                    {countries.map((country) => (
                      <button
                        key={country.id}
                        onClick={() => setSelectedCountry(country)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedCountry?.id === country.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`country-${country.code}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{country.flag}</span>
                          <div>
                            <div className="font-medium">{country.name}</div>
                            <div className="text-xs text-gray-500">
                              {getCountryCities().length} şehir
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Cities Column */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {selectedCountry ? `${selectedCountry.flag} ${selectedCountry.name} - Şehirler` : 'Şehirler'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedCountry ? (
                  <div className="text-center py-12 text-gray-500">
                    <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Sol sütundan bir ülke seçin</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Add City Form */}
                    <form onSubmit={handleAddCity} className="flex gap-2">
                      <Input
                        value={newCityName}
                        onChange={(e) => setNewCityName(e.target.value)}
                        placeholder="Şehir adı yazın ve Enter'a basın"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCity(e);
                          }
                        }}
                        data-testid="new-city-input"
                      />
                      <Button type="submit" size="icon" data-testid="add-city-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </form>

                    {/* Cities List */}
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2">
                        {getCountryCities().map((city) => (
                          <div
                            key={city.id}
                            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                            data-testid={`city-${city.id}`}
                          >
                            <span className="font-medium">{city.name}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteCity(city.id)}
                              data-testid={`delete-city-${city.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Venue Centers Tab */}
        <TabsContent value="venues" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Venue Form */}
            <Card>
              <CardHeader>
                <CardTitle>Yeni Fuar Merkezi Ekle</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddVenue} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Şehir Seç *</Label>
                    <Select
                      value={selectedCity}
                      onValueChange={setSelectedCity}
                      required
                    >
                      <SelectTrigger data-testid="venue-city-select">
                        <SelectValue placeholder="Şehir seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map(city => {
                          const country = countries.find(c => c.id === city.country_id);
                          return (
                            <SelectItem key={city.id} value={city.id}>
                              {city.name} {country ? `(${country.flag} ${country.name})` : ''}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Fuar Merkezi Adı *</Label>
                    <Input
                      value={newVenue.name}
                      onChange={(e) => setNewVenue({...newVenue, name: e.target.value})}
                      placeholder="İstanbul Fuar Merkezi"
                      required
                      data-testid="venue-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Adres (Opsiyonel)</Label>
                    <Textarea
                      value={newVenue.address}
                      onChange={(e) => setNewVenue({...newVenue, address: e.target.value})}
                      placeholder="Yeşilköy, İstanbul"
                      data-testid="venue-address-input"
                    />
                  </div>

                  <Button type="submit" className="w-full" data-testid="add-venue-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Fuar Merkezi Ekle
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Venues List */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedCity ? 
                    `${cities.find(c => c.id === selectedCity)?.name} - Fuar Merkezleri` : 
                    'Fuar Merkezleri'
                  }
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedCity ? (
                  <div className="text-center py-12 text-gray-500">
                    <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Şehir seçin veya tüm fuar merkezlerini görmek için listeyi açın</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-3">
                      {getCityVenues().length === 0 ? (
                        <p className="text-center text-gray-500 py-8">
                          Bu şehirde henüz fuar merkezi yok
                        </p>
                      ) : (
                        getCityVenues().map((venue) => (
                          <div
                            key={venue.id}
                            className="p-4 rounded-lg border border-gray-200 hover:bg-gray-50"
                            data-testid={`venue-${venue.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-medium">{venue.name}</div>
                                {venue.address && (
                                  <div className="text-sm text-gray-500 mt-1">{venue.address}</div>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteVenue(venue.id)}
                                data-testid={`delete-venue-${venue.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
